import React from 'react';
import Paper from '@material-ui/core/Paper';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, AppBar, Toolbar, Typography, CircularProgress, IconButton, RadioGroup, FormControlLabel, Radio, FormControl, Card, CardContent, CardActionArea, CardMedia } from '@material-ui/core';
import DealsContext, { DealLineCollection, LinesData } from './DealsContext';
import { LineSystemCollection, SystemButtonStateCollection } from './SystemsContext';
import { Box, Button, Grid } from '@material-ui/core';
import { AuthContext } from '../authentication/AuthContext';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { IdSet, LicenseRequestData, LicenseRequestStatus, LinesView, SystemCallbackType } from './Paperbase';
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import { MuiStyles } from './styling/MuiStyles';
import { LicenseSort } from "../general/sorting/LicenseSort";
import { SortOrder, SortBy, SortView, SortViewData, SortData } from "../general/sorting/LicenseSortParams";
import ImportExportIcon from '@material-ui/icons/ImportExport';
import { useHistory } from 'react-router';
import { NavigatorData } from './NavigatorData';
import WarningIcon from '@material-ui/icons/Warning';
import ActivateEvalLicense from './ActivateEvalLicense';
import { ProductsDataSet } from './DownloadProductData';
import { EvalStatus } from './RequestEvaluation';
import { SystemActivationData } from './ActivateLicense';


export interface DealType {
  "accountName": string,
  "dealId": string,
  "dealType": string,
  "licenseType": string,
  "lineCount": number,
  "supportRenewalDate": string,
  "dealViewExpanded"?: boolean
}

export type DealCollection = { [name: string]: DealType }

export type RequestAccessType = {
  message: JSX.Element,
  button: JSX.Element
}

const styles = (theme: Theme) =>
  createStyles({
    paper: {
      margin: 'auto',
      overflow: 'hidden',
    },
    searchBar: {
      borderBottom: '1px solid '+ MyDeltaColors.MERCURY,
      backgroundColor: DeltaXMLColors.CONCRETE
    },
    contentWrapper: {
      margin: '40px 16px',
    },
    dealsRow: {
      background: DeltaXMLColors.OXFORD_BLUE,
    },
    dealsRowCellText: {
      color: MyDeltaColors.WHITE,
      cursor: 'pointer'
    },
    buttonRefresh: {
      float: 'right',
    },
    alertBar: {
      backgroundColor: MyDeltaColors.HAWKES_BLUE,
      borderRadius: 5,
    },
    gridContainer: {
      margin: 5
    },
    gridItem: {
      marginTop: 10
    },
    tableContainer: {
      borderRadius: 0,
      maxHeight: 'calc(100vh - 170px)'
    },
    progress: {
      float: 'right',
      margin: '2px 8px 0px 0px'
    },
    icon: {
      color: DeltaXMLColors.FOUNTAIN_BLUE
    },
    sortButton: {
      color: MyDeltaColors.WHITE,
      backgroundColor: 'transparent',
      padding: '3px',
      marginRight: '5px'
    },
    infoBody: {
      margin: '10px 0px 10px 0px',
    },
    card: {
      boxShadow: 'none'
    },
    linkStyle: {
      color: DeltaXMLColors.FOUNTAIN_BLUE,
      marginTop: '10px'
    },
    warningIcon: {
      color: MyDeltaColors.ORANGE,
      marginRight: '10px'
    },
    progressAlertBar: {
      backgroundColor: MyDeltaColors.ORANGE_BACKGROUND,
      color: DeltaXMLColors.OXFORD_BLUE
    },
  });

declare global {
  interface Window { ATL_JQ_PAGE_PROPS: any; }
}

window.ATL_JQ_PAGE_PROPS = window.ATL_JQ_PAGE_PROPS || {};

export const SUBSCRIPTIONS_LICENSE_TYPE: string = "241 SaaS Subscriptions";
export const EVALUATIONS_DEAL_IDENTIFIER: string = "Hosted-Evaluation";
export const EVALUATIONS_LICENSE_TYPE: string = "EVALUATION"

export interface ContentProps extends WithStyles<typeof styles> {
  licenseProgressNumber: number,
  dealsData: DealCollection,
  dealLinesData: DealLineCollection,
  linesSystemData: LineSystemCollection,
  licenseRequestState: LicenseRequestData,
  systemButtons: SystemButtonStateCollection;
  callSystemAction: SystemCallbackType;
  createPartialFulfillmentSystem: (idSet: IdSet, fulfillmentSize: number) => void;
  handleDealOpen: (id: string, view: LinesView) => void;
  handleLineOpen: (dealId: string, lineId: string, view: LinesView) => void;
  handleRefresh: () => void;
  downloadLicense: (dealId: string, lineNumber: string, systemId: string, licenseModel: string) => void;
  addUserAnnotationToSystem: (idSet: IdSet, user_annotation: string) => void;
  handleChangeForLinesView: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSortViewChange: (sortView: SortView, sortData: SortData) => void;
  sortViewData: SortViewData;
  linesView: LinesView;
  enableExpiredViewOption: boolean;
  dateFormat: string;
  timeFormat: string;
  evalUnactivatedDealId?: string,
  evalUnactivatedLine?: LinesData,
  evalActivateDialogOpen?: boolean,
  evalActivateStatus?: EvalStatus,
  downloadProductsData?: ProductsDataSet,
  handleUnactivatedEvalDialogClose?: () => void,
  handleEvalActivateAndDownload?: (data: SystemActivationData) => void
}

function LicensesContent(props: ContentProps) {
  const { licenseProgressNumber, dealsData, classes, dealLinesData, linesSystemData, sortViewData, handleSortViewChange, handleDealOpen, handleLineOpen, licenseRequestState, handleRefresh, systemButtons, callSystemAction, createPartialFulfillmentSystem, downloadLicense, addUserAnnotationToSystem, handleChangeForLinesView, linesView, enableExpiredViewOption, dateFormat, timeFormat,
   evalActivateDialogOpen, evalUnactivatedDealId, evalUnactivatedLine, downloadProductsData, handleUnactivatedEvalDialogClose, handleEvalActivateAndDownload, evalActivateStatus} = props;
  const auth = React.useContext(AuthContext);
  const sortData = sortViewData[SortView.DEALS];

  React.useEffect(() => {
    const collectorScript = document.createElement('script');
    collectorScript.src = "https://deltaxml.atlassian.net/s/d41d8cd98f00b204e9800998ecf8427e-T/o2joag/b/24/e73395c53c3b10fde2303f4bf74ffbf6/_/download/batch/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector/com.atlassian.jira.collector.plugin.jira-issue-collector-plugin:issuecollector.js?locale=en-GB&collectorId=00510c64";
    collectorScript.async = true;
    document.body.appendChild(collectorScript);

    const requestButtonScript = document.createElement('script');
    requestButtonScript.type = "text/javascript";
    requestButtonScript.text = 'window.ATL_JQ_PAGE_PROPS={"triggerFunction":function(showCollectorDialog){jQuery("#requestButton").click(function(e){e.preventDefault();showCollectorDialog()})}}';
    document.body.appendChild(requestButtonScript);
    return () => {
      document.body.removeChild(collectorScript);
      document.body.removeChild(requestButtonScript);
    }
  }, []);

  const dealsContextComponents = Object.entries(dealsData)
    .sort((a: any, b: any) => LicenseSort.sortLicensesTable(a, b, sortData.order, sortData.sortBy))
    .map((kvp: [string, DealType]) => {

      const [name, value] = kvp;
      return <DealsContext key={name} linesView={linesView} enableSystemActionButtons={enableExpiredViewOption} dealId={name} row={value} lines={dealLinesData[name]}
                           sortViewData={sortViewData} handleSortViewChange={handleSortViewChange} handleDealOpen={handleDealOpen} handleLineOpen={handleLineOpen}
                           linesSystemData={linesSystemData} dealOpen={!!dealsData[name]['dealViewExpanded']} systemButtons={systemButtons}
                           callSystemAction={callSystemAction} createPartialFulfillmentSystem={createPartialFulfillmentSystem} downloadLicense={downloadLicense}
                           addUserAnnotationToSystem={addUserAnnotationToSystem} dateFormat={dateFormat} timeFormat={timeFormat} />
    });

  let msg = 'license data not yet requested';
  // error message:
  const { deals, lines, systems } = licenseRequestState;
  if (deals.status === LicenseRequestStatus.notYetRequested) {
    msg = 'Authenticating...';
  } if (deals.status === LicenseRequestStatus.errorResponse) {
    msg = 'Error encountered when requesting deals';
  } else if (lines.status === LicenseRequestStatus.errorResponse) {
    msg = 'Error encountered when requesting lines';
  } else if (systems.status === LicenseRequestStatus.errorResponse) {
    msg = 'Error encountered when requesting systems';
  } else if (deals.status === LicenseRequestStatus.inProgress) {
    msg = 'Requesting deals #' + licenseProgressNumber + '...';
  } else if (lines.status === LicenseRequestStatus.inProgress) {
    msg = 'Requesting lines #' + licenseProgressNumber + '...';
  } else if (systems.status === LicenseRequestStatus.inProgress) {
    msg = 'Requesting systems #' + licenseProgressNumber + '...';
  } else {
    msg = '';
  }
  msg = msg === '' ? msg : '[' + msg + ']';

  const dealsAvailable = deals.status !== LicenseRequestStatus.noItems && (Object.entries(dealsData).length > 0);
  const showProgressBar = systems.status === LicenseRequestStatus.inProgress ||
    deals.status === LicenseRequestStatus.inProgress ||
    lines.status === LicenseRequestStatus.inProgress;

  const onSortChange = (sortBy: SortBy) => {
    let nextSort = sortData.order === SortOrder.DESCENDING ?
      SortOrder.ASCENDING :
      SortOrder.DESCENDING;

    handleSortViewChange(SortView.DEALS, { order: nextSort, sortBy: sortBy} )
  }

  const handleBookMeeting = () => {
    window.open("https://calendly.com/oliver-partridge", '_blank', 'noopener,noreferrer');
  }

  const isSubscriptionView = linesView === LinesView.CURRENT_SUBS || linesView === LinesView.HISTORIC_SUBS;
  const isEvaluationView = linesView === LinesView.CURRENT_EVALS || linesView === LinesView.HISTORIC_EVALS;
  
  let radioButton, licenseHeaderText;
  let requestAccess : RequestAccessType;
  const history = useHistory();

  if (isSubscriptionView) {
    radioButton = (
      <RadioGroup row aria-label="Conflicts" id="subsRadioGroup" name="subsRadioGroup" value={linesView} onChange={(event) => handleChangeForLinesView(event)}>
        <FormControlLabel value={LinesView.CURRENT_SUBS} control={<Radio />} label="Current Subscriptions" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
        <FormControlLabel value={LinesView.HISTORIC_SUBS} control={<Radio />} label="Expired Subscriptions" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
      </RadioGroup>
    );
    requestAccess = {
      message: <Typography align="left" color="primary" variant="body1" >
        <p>You currently do not have access to any subscriptions.</p>
        <p>If you wish to try one of our SaaS products, please click on the <i>Request Subscription</i> button below.</p>
        <p>You can then subscribe to a range of products like DeltaJSON, Content Compare S1000D, Content Compare JATS, Content Compare BITS, Content Compare NISO-STS or Content Compare XSL-FO.</p>
      </Typography>,
      button: <Button variant="contained" color="primary" id="requestSubsButton" onClick={() => history.push(NavigatorData.saasProductsBaseUrl)}>Request Subscription</Button>
    };
    licenseHeaderText = 'Subscriptions';
  } else if (isEvaluationView) {
    radioButton = (
      <RadioGroup row aria-label="Conflicts" id="evalsRadioGroup" name="evalsRadioGroup" value={linesView} onChange={(event) => handleChangeForLinesView(event)}>
        <FormControlLabel value={LinesView.CURRENT_EVALS} control={<Radio />} label="Current Evaluations" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
        <FormControlLabel value={LinesView.HISTORIC_EVALS} control={<Radio />} label="Expired Evaluations" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
      </RadioGroup>
    );
    requestAccess = {
      message: <Typography align="left" color="primary" variant="body1" >
        <p>You currently do not have any active evaluations.</p>
        <p>If you wish to try one of our products, please contact support at <a style={{color: DeltaXMLColors.FOUNTAIN_BLUE}} href="mailto:support@deltaxignia.com?subject=Request evaluation">support@deltaxignia.com</a>.</p>
        <p>Once you have the trial license, please follow the steps mentioned below to start evaluating.</p>
      </Typography>,
      button: <></>
    };
    licenseHeaderText = 'Evaluations';
  } else {
    radioButton = (
      <RadioGroup row aria-label="Conflicts" id="licenseRadioGroup" name="licenseRadioGroup" value={linesView} onChange={(event) => handleChangeForLinesView(event)}>
        <FormControlLabel value={LinesView.CURRENT} control={<Radio />} label="Current Licenses" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
        <FormControlLabel value={LinesView.HISTORIC} control={<Radio />} label="Expired Licenses" style={{ color: DeltaXMLColors.OXFORD_BLUE }} />
      </RadioGroup>
    );
    requestAccess = {
      message: <Typography align="left" color="primary" variant="body1" >
        <p>You currently do not have access to any purchased licenses.</p>
        <p>If you have not submitted a request already to get access to your organization's licenses, please click on the <i>Request Access</i> button below.</p>
        <p>A confirmation email with the request details will be sent to your email address.</p>
      </Typography>,
      button: <Button variant="contained" color="primary" id="requestButton">Request Access</Button>
    };
    licenseHeaderText = 'License Agreements';
  }
  
  return (
    <Paper className={classes.paper}>
      <AppBar className={classes.searchBar} position="static" color="default" elevation={0}>
        <Toolbar>
          <Grid container spacing={0} alignItems="center" style={{padding: '10px 0 5px 0'}}>
              <Grid item xs={4}>
                <Typography variant="h4" style={{color: DeltaXMLColors.OXFORD_BLUE}}>
                  {licenseHeaderText}
                </Typography>
              </Grid>
              {(dealsAvailable) &&
                <React.Fragment>
                  <Grid item xs={5}>
                    <Typography color="textSecondary">
                      {msg}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                  <Button
                      variant="contained"
                      color="primary"
                      className={classes.buttonRefresh}
                      onClick={handleRefresh}>
                      Refresh
                    </Button>
                    {/* Removed 'Refresh' button that was here because it does not preserve
                    expanded line items as originally intended. So the browser refresh button
                    performs the same function now.
                     */}
                    {showProgressBar &&
                      <CircularProgress className={classes.progress} variant="indeterminate" size="30px" />
                    }
                  </Grid>
                  <Grid item>
                    <FormControl variant="outlined" disabled={!enableExpiredViewOption}>
                      {radioButton}
                    </FormControl>
                  </Grid>
                </React.Fragment>
              }
          </Grid>
        </Toolbar>
      </AppBar>
      {(showProgressBar) &&
        <div className={classes.contentWrapper}>
          <AppBar className={classes.progressAlertBar} position="static" elevation={0}>
            <Typography align="center" component="div">
              <Grid container className={classes.gridContainer}>
                <Grid item xs={1} className={classes.gridItem}>
                  <WarningIcon fontSize="large" className={classes.warningIcon} />
                </Grid>
                <Grid item />
                <Grid item xs={10}>
                  <Typography align="left" color="primary" variant="body1">
                    <p>Please wait while we fetch deals information for your account...</p>
                  </Typography>
                </Grid>
              </Grid>
            </Typography>
          </AppBar>
        </div>
      }
      {(!dealsAvailable && !showProgressBar) &&
        <div className={classes.contentWrapper}>
          <AppBar className={classes.alertBar} position="static" elevation={0}>
            <Typography align="center" component="div">
              <Grid container className={classes.gridContainer}>
                <Grid item xs={1} className={classes.gridItem}>
                  <InfoOutlinedIcon fontSize="large" className={classes.icon} />
                </Grid>
                <Grid item />
                <Grid item xs={10}>
                  {requestAccess.message}
                </Grid>
              </Grid>
            </Typography>
          </AppBar>
          {auth.isAuthenticated() &&
            <Box justifyContent="center" display="flex" my="1rem">{requestAccess.button}</Box>
          }
        </div>}
      {(dealsAvailable) &&
        <TableContainer className={classes.tableContainer}>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow className={classes.dealsRow}>
                <TableCell />
                <TableCell className={classes.dealsRowCellText}>
                  <MuiStyles.LightTooltip title="The name of the licensed organisation" placement="bottom-start">
                    <Grid container direction="row" onClick={() => onSortChange(SortBy.ORGANIZATION)}>
                      <Grid item>
                        <IconButton className={classes.sortButton} size="small">
                          <ImportExportIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <Typography>Organisation</Typography>
                      </Grid>
                    </Grid>
                  </MuiStyles.LightTooltip>
                </TableCell>
                <TableCell className={classes.dealsRowCellText}>
                  <MuiStyles.LightTooltip title="An identifier for this agreement. If you have an invoice, this number will be mentioned on it" placement="bottom-start">
                    <Grid container direction="row" onClick={() => onSortChange(SortBy.DEAL)}>
                      <Grid item>
                        <IconButton className={classes.sortButton} size="small">
                          <ImportExportIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <Typography>Agreement Number</Typography>
                      </Grid>
                    </Grid>
                  </MuiStyles.LightTooltip>
                </TableCell>
                <TableCell className={classes.dealsRowCellText}>
                  <MuiStyles.LightTooltip title="The type of relationship you have with DeltaXML" placement="bottom-start">
                    <Grid container direction="row" onClick={() => onSortChange(SortBy.DEAL_TYPE)}>
                      <Grid item>
                        <IconButton className={classes.sortButton} size="small">
                          <ImportExportIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <Typography>Agreement Type</Typography>
                      </Grid>
                    </Grid>
                  </MuiStyles.LightTooltip>
                </TableCell>
                <TableCell className={classes.dealsRowCellText}>
                  <MuiStyles.LightTooltip title="The date on which this agreement is scheduled for renewal" placement="bottom-start">
                    <Grid container direction="row" onClick={() => onSortChange(SortBy.RENEWAL_DATE)}>
                      <Grid item>
                        <IconButton className={classes.sortButton} size="small">
                          <ImportExportIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <Typography>Renewal Date</Typography>
                      </Grid>
                    </Grid>
                  </MuiStyles.LightTooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dealsContextComponents}
            </TableBody>
          </Table>
        </TableContainer>}
        {(isEvaluationView) &&
        <Grid container style={{marginTop: '10px'}}>
          {(evalUnactivatedDealId) && 
          <ActivateEvalLicense dealId={evalUnactivatedDealId!} line={evalUnactivatedLine!} downloadProductsData={downloadProductsData!} dialogOpen={evalActivateDialogOpen!} handleCloseDialog={handleUnactivatedEvalDialogClose!} dateFormat={dateFormat} handleActivateAndDownload={handleEvalActivateAndDownload!} activateStatus={evalActivateStatus!}/> 
          }
          <Grid item xs={9}>
            <Card className={classes.card}>
              <CardActionArea>
                <a href='https://www.youtube.com/playlist?list=PLf-I1kkv6W6PkcY7mJXQ9yVBtEoW-6SbH' target="_blank" rel="noreferrer">
                  <CardMedia
                    component="img"
                    image='/Evalgettingstartedbanner.jpg'
                    title="Evalgettingstarted"
                  />
                </a>
              </CardActionArea>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h3" style={{ fontWeight: "bold", marginBottom: "20px" }} color="textPrimary">
                  Quick Links
                </Typography>
                <Typography variant="h6" className={classes.infoBody}>
                  <a className={classes.linkStyle} href='https://docs.deltaxignia.com/support-and-documentation/latest/how-to-determine-a-server-or-named-user-hostid#id-(latest)CreatingSystems-HostIDs,IPAddress,MacAddress-Activatingyourlicense' target="_blank" rel="noreferrer">How to find your Host ID</a>
                </Typography>
                <Typography variant="h6" className={classes.infoBody}>
                  <a className={classes.linkStyle} href='https://aws.amazon.com/marketplace/seller-profile?id=6f42a3ba-0a10-4ba5-bfe7-9318fa52fdfa&ref=dtl_prodview-hiv3jzgceb7pu' target="_blank" rel="noreferrer">Go to our AWS AMI service</a>
                </Typography>
                <Typography variant="h6" className={classes.infoBody}>
                  <a className={classes.linkStyle} href='https://docs.deltaxignia.com/' target="_blank" rel="noreferrer">Documentation site</a>
                </Typography>
                <Typography variant="h6" className={classes.infoBody}>
                  <a className={classes.linkStyle} href='https://deltaxml.atlassian.net/servicedesk/customer/portal/5' target="_blank" rel="noreferrer">Evaluation support portal</a>
                </Typography>
                <Typography variant="h6" className={classes.infoBody}>
                  <a className={classes.linkStyle} href='https://www.youtube.com/playlist?list=PLf-I1kkv6W6PkcY7mJXQ9yVBtEoW-6SbH' target="_blank" rel="noreferrer">Getting started playlist</a>
                </Typography>
                <Typography variant="h6" style={{ fontWeight: "bold", marginTop: '40px' }}>
                  Book a meeting for a guided installation
                </Typography>
                <Typography variant="body2" color="textSecondary" className={classes.infoBody}>
                  Book a 30 minute session with one of our customer success experts. They will take you through the product and the installation process step by step.
                </Typography>
                <Button variant="contained" color="primary" className={classes.infoBody} onClick={() => handleBookMeeting()}>Book guided set-up</Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        }
    </Paper>);
}

export default withStyles(styles)(LicensesContent);


